import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { gql } from '../../../graphql/client';
import {
  M_DELETE_VIEW_HIERARCHY,
  M_INSERT_VIEW,
  M_INSERT_VIEW_VIEW,
  M_SWAP_CHILD_SORTS,
} from '../../../graphql/operations';
import type { ChildViewEdge, View } from '../../../types/forge';
import { AssetPicker } from '../../assets/AssetPicker';
import { saveView } from '../save-pipeline/savePipeline';
import { useEditorStore } from '../store/editorStore';

/**
 * Inline editor for `TabNavigator` / `StackNavigator` routes.
 *
 * Authors typically don't want to drag-drop Screens under a navigator
 * and then individually edit each one's name + tab icon. This editor
 * collapses that workflow into a single list:
 *
 *   - one row per route (Screen / StackNavigator / TabNavigator child)
 *   - inline name edit (saved on blur)
 *   - icon control: emoji string OR pick-from-library URL
 *     (the engine's TabNavigator detects URL vs emoji at render time)
 *   - reorder via ↑/↓
 *   - delete (with the descendant view subtree)
 *   - "+ Add Screen" / "+ Add Stack" creates the underlying View rows
 *     and links them under the navigator, picking the right starting
 *     name + sort
 *
 * The component is a pure consumer of `appData` (the same blob the
 * Canvas / ViewTree consume). Every mutation invalidates `app-data` so
 * the canvas + tree stay in lockstep.
 */
export interface NavigatorRoutesEditorProps {
  appId: string;
  navigator: View;
  /**
   * Flat list of all the app's views — used to resolve child edges
   * back to their actual View rows. Coming from the same `app-data`
   * query that powers the Canvas; passed in so we don't redundantly
   * refetch or lose the cache.
   */
  appViews: View[];
}

const ROUTE_TYPES = new Set(['Screen', 'StackNavigator', 'TabNavigator']);

export function NavigatorRoutesEditor({
  appId,
  navigator,
  appViews,
}: NavigatorRoutesEditorProps) {
  const queryClient = useQueryClient();
  const selectView = useEditorStore((s) => s.selectView);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['app-data', appId] });
  };

  // Resolve current route edges → route View rows, sorted by edge.sort.
  const edges = [...(navigator.child_views ?? [])].sort((a, b) => a.sort - b.sort);
  const routes = edges
    .map((edge) => {
      const view = appViews.find((v) => v.id === edge.related_view_id);
      if (!view) return null;
      if (!ROUTE_TYPES.has(view.Type)) return null;
      return { edge, view };
    })
    .filter(Boolean) as { edge: ChildViewEdge; view: View }[];

  /* ----------------------------- Add a route ---------------------------- */

  const addScreen = useMutation({
    mutationFn: async () => {
      const nextSort =
        edges.length === 0 ? 0 : Math.max(...edges.map((e) => e.sort)) + 1;
      const screenName = nextScreenName(routes.map((r) => r.view.name));
      const created = await gql<{ insert_View_one: View }>(M_INSERT_VIEW, {
        appId,
        type: 'Screen',
        name: screenName,
        sort: nextSort,
      });
      await gql(M_INSERT_VIEW_VIEW, {
        parentId: navigator.id,
        childId: created.insert_View_one.id,
        sort: nextSort,
      });
      return created.insert_View_one.id;
    },
    onSuccess(newId) {
      invalidate();
      selectView(newId);
    },
  });

  const addStack = useMutation({
    mutationFn: async () => {
      const nextSort =
        edges.length === 0 ? 0 : Math.max(...edges.map((e) => e.sort)) + 1;
      const stackName = nextStackName(routes.map((r) => r.view.name));
      const stack = await gql<{ insert_View_one: View }>(M_INSERT_VIEW, {
        appId,
        type: 'StackNavigator',
        name: stackName,
        sort: nextSort,
      });
      await gql(M_INSERT_VIEW_VIEW, {
        parentId: navigator.id,
        childId: stack.insert_View_one.id,
        sort: nextSort,
      });
      // Seed the stack with one initial Screen so it's not empty.
      const screen = await gql<{ insert_View_one: View }>(M_INSERT_VIEW, {
        appId,
        type: 'Screen',
        name: `${stackName} Home`,
        sort: 0,
      });
      await gql(M_INSERT_VIEW_VIEW, {
        parentId: stack.insert_View_one.id,
        childId: screen.insert_View_one.id,
        sort: 0,
      });
      return stack.insert_View_one.id;
    },
    onSuccess(newId) {
      invalidate();
      selectView(newId);
    },
  });

  /* ------------------------- Reorder / delete -------------------------- */

  const swap = useMutation({
    mutationFn: async ({ aIdx, bIdx }: { aIdx: number; bIdx: number }) => {
      const a = edges[aIdx]!;
      const b = edges[bIdx]!;
      await gql(M_SWAP_CHILD_SORTS, {
        aId: a.id,
        aSort: b.sort,
        bId: b.id,
        bSort: a.sort,
      });
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: ({ viewId }: { viewId: string }) =>
      gql(M_DELETE_VIEW_HIERARCHY, { viewId }),
    onSuccess: invalidate,
  });

  return (
    <div style={s.root}>
      <h3 style={s.heading}>Routes</h3>
      {routes.length === 0 && (
        <p style={s.empty}>
          No routes yet. Add a Screen below — for tabbed flows, "Add Stack"
          creates a tab that owns its own back-stack.
        </p>
      )}

      <ul style={s.list}>
        {routes.map((route, idx) => (
          <RouteRow
            key={route.view.id}
            route={route}
            isFirst={idx === 0}
            isLast={idx === routes.length - 1}
            onMoveUp={() => swap.mutate({ aIdx: idx, bIdx: idx - 1 })}
            onMoveDown={() => swap.mutate({ aIdx: idx, bIdx: idx + 1 })}
            onDelete={() => {
              if (
                window.confirm(
                  `Delete "${route.view.name}" and everything inside it?`,
                )
              ) {
                remove.mutate({ viewId: route.view.id });
              }
            }}
            onSelect={() => selectView(route.view.id)}
            onChanged={invalidate}
          />
        ))}
      </ul>

      <div style={s.addRow}>
        <button
          type="button"
          onClick={() => addScreen.mutate()}
          disabled={addScreen.isPending}
          style={s.addBtn}
        >
          {addScreen.isPending ? 'Adding…' : '+ Add Screen'}
        </button>
        <button
          type="button"
          onClick={() => addStack.mutate()}
          disabled={addStack.isPending}
          style={s.addBtnSecondary}
          title="Adds a tab/route that owns its own back-stack of screens"
        >
          {addStack.isPending ? 'Adding…' : '+ Add Stack'}
        </button>
      </div>
    </div>
  );
}

/* ---------------------------- Single row ---------------------------- */

function RouteRow({
  route,
  isFirst,
  isLast,
  onMoveUp,
  onMoveDown,
  onDelete,
  onSelect,
  onChanged,
}: {
  route: { edge: ChildViewEdge; view: View };
  isFirst: boolean;
  isLast: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  onSelect: () => void;
  onChanged: () => void;
}) {
  const { view } = route;
  const props = (view.viewStyle?.properties ?? {}) as Record<string, unknown>;
  const initialIcon = typeof props.tabBarIcon === 'string' ? props.tabBarIcon : '';

  const [name, setName] = useState(view.name);
  const [icon, setIcon] = useState(initialIcon);
  const [pickerOpen, setPickerOpen] = useState(false);
  const lastSavedName = useRef(view.name);
  const lastSavedIcon = useRef(initialIcon);

  // Reset local buffers if the underlying view changes (e.g. server refetch).
  useEffect(() => {
    setName(view.name);
    lastSavedName.current = view.name;
  }, [view.id, view.name]);
  useEffect(() => {
    setIcon(initialIcon);
    lastSavedIcon.current = initialIcon;
  }, [view.id, initialIcon]);

  async function commitName(next: string): Promise<void> {
    if (next === lastSavedName.current) return;
    if (!next.trim()) {
      setName(lastSavedName.current);
      return;
    }
    await saveView({ viewId: view.id, patch: { name: next } });
    lastSavedName.current = next;
    onChanged();
  }

  async function commitIcon(next: string): Promise<void> {
    if (next === lastSavedIcon.current) return;
    const newProps = { ...props, tabBarIcon: next || undefined };
    if (!next) delete newProps.tabBarIcon;
    await saveView({
      viewId: view.id,
      patch: {
        viewStyle: view.viewStyle
          ? { ...view.viewStyle, properties: newProps }
          : { id: '', properties: newProps },
      },
    });
    lastSavedIcon.current = next;
    onChanged();
  }

  return (
    <li style={s.row}>
      <div style={s.reorderCol}>
        <button
          type="button"
          onClick={onMoveUp}
          disabled={isFirst}
          style={isFirst ? s.reorderBtnDisabled : s.reorderBtn}
          title="Move up"
        >
          ↑
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={isLast}
          style={isLast ? s.reorderBtnDisabled : s.reorderBtn}
          title="Move down"
        >
          ↓
        </button>
      </div>

      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        style={s.iconWell}
        title="Click to pick from library"
      >
        {icon ? (
          looksLikeUrl(icon) ? (
            <img
              src={icon}
              alt=""
              style={s.iconThumb}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.opacity = '0.2';
              }}
            />
          ) : (
            <span style={s.iconEmoji}>{icon}</span>
          )
        ) : (
          <span style={s.iconEmpty}>+</span>
        )}
      </button>

      <div style={s.bodyCol}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => {
            void commitName(name);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
            if (e.key === 'Escape') setName(lastSavedName.current);
          }}
          onFocus={onSelect}
          style={s.nameInput}
        />
        <input
          value={icon}
          onChange={(e) => setIcon(e.target.value)}
          onBlur={() => {
            void commitIcon(icon);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
            if (e.key === 'Escape') setIcon(lastSavedIcon.current);
          }}
          placeholder="emoji or URL — click the well to pick"
          style={s.iconInput}
        />
        <span style={s.typeTag}>{view.Type}</span>
      </div>

      <button type="button" style={s.deleteBtn} onClick={onDelete} title="Delete">
        ×
      </button>

      <AssetPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        kind="icon"
        title="Select Icon"
        onPick={(url) => {
          setIcon(url);
          void commitIcon(url);
        }}
      />
    </li>
  );
}

/* ---------------------------- Helpers ---------------------------- */

function looksLikeUrl(s: string): boolean {
  return /^https?:\/\//i.test(s) || s.startsWith('//') || s.startsWith('/');
}

function nextScreenName(existing: string[]): string {
  for (let i = 1; i < 1000; i++) {
    const candidate = `Screen ${i}`;
    if (!existing.includes(candidate)) return candidate;
  }
  return `Screen ${Date.now() % 10000}`;
}

function nextStackName(existing: string[]): string {
  for (let i = 1; i < 1000; i++) {
    const candidate = `Stack ${i}`;
    if (!existing.includes(candidate)) return candidate;
  }
  return `Stack ${Date.now() % 10000}`;
}

const s: Record<string, React.CSSProperties> = {
  root: { padding: 0 },
  heading: { fontSize: 12, fontWeight: 600, color: '#475569', margin: '0 0 8px' },
  empty: { fontSize: 12, color: '#94A3B8', margin: '0 0 12px' },
  list: { listStyle: 'none', padding: 0, margin: '0 0 12px', display: 'flex', flexDirection: 'column', gap: 8 },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: 8,
    backgroundColor: '#fff',
    border: '1px solid #E2E8F0',
    borderRadius: 6,
  },
  reorderCol: { display: 'flex', flexDirection: 'column', gap: 2 },
  reorderBtn: {
    width: 22,
    height: 18,
    fontSize: 11,
    border: '1px solid #CBD5E1',
    backgroundColor: '#fff',
    color: '#475569',
    borderRadius: 3,
    cursor: 'pointer',
    padding: 0,
  },
  reorderBtnDisabled: {
    width: 22,
    height: 18,
    fontSize: 11,
    border: '1px solid #E2E8F0',
    backgroundColor: '#F8FAFC',
    color: '#CBD5E1',
    borderRadius: 3,
    cursor: 'not-allowed',
    padding: 0,
  },
  iconWell: {
    width: 36,
    height: 36,
    border: '1px solid #CBD5E1',
    borderRadius: 6,
    backgroundColor: '#F8FAFC',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    padding: 0,
    flexShrink: 0,
  },
  iconThumb: { width: '100%', height: '100%', objectFit: 'cover' },
  iconEmoji: { fontSize: 20 },
  iconEmpty: { fontSize: 18, color: '#94A3B8' },
  bodyCol: { flex: 1, display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  nameInput: {
    padding: '4px 6px',
    fontSize: 12,
    fontWeight: 600,
    color: '#0F172A',
    border: '1px solid transparent',
    borderRadius: 3,
    backgroundColor: 'transparent',
  },
  iconInput: {
    padding: '3px 6px',
    fontSize: 11,
    color: '#64748B',
    border: '1px solid #E2E8F0',
    borderRadius: 3,
    fontFamily: 'ui-monospace, monospace',
  },
  typeTag: {
    fontSize: 10,
    color: '#94A3B8',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.4,
    paddingLeft: 6,
  },
  deleteBtn: {
    width: 22,
    height: 22,
    fontSize: 14,
    border: 'none',
    backgroundColor: 'transparent',
    color: '#94A3B8',
    cursor: 'pointer',
    padding: 0,
  },
  addRow: { display: 'flex', gap: 8 },
  addBtn: {
    flex: 1,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#4F46E5',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  },
  addBtnSecondary: {
    flex: 1,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: '#4F46E5',
    backgroundColor: '#fff',
    border: '1px solid #C7D2FE',
    borderRadius: 6,
    cursor: 'pointer',
  },
};
